<script setup>
import { ref, computed } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import Icon from '@/Components/UI/Icon.vue'
import ConfirmDialog from '@/Components/UI/ConfirmDialog.vue'
import StatusBadge from '@/Components/UI/StatusBadge.vue'
import ActionCluster from '@/Components/UI/ActionCluster.vue'
import CustomerChannels from '@/Components/CustomerChannels.vue'
import { useCurrency } from '@/composables/useCurrency.js'

defineOptions({ layout: AppLayout })

const props = defineProps({
  customer:       { type: Object, required: true },
  lifetime_stats: { type: Object, default: () => ({}) },
  channels:       { type: Array,  default: () => [] },
})

const { t } = useI18n()
const { formatDate } = useCurrency()

const showArchiveConfirm = ref(false)

const licenceBadgeStyle = (status) => ({
  valid:   { background: 'var(--okSoft)',     color: 'var(--ok)'     },
  expired: { background: 'var(--dangerSoft)', color: 'var(--danger)' },
  none:    { background: 'var(--surfaceAlt)', color: 'var(--muted)'  },
}[status] || { background: 'var(--surfaceAlt)', color: 'var(--muted)' })

const headerActions = computed(() => [
  {
    key: 'newRental',
    label: t('rentals.new'),
    icon: 'plus',
    variant: 'primary',
    onClick: () => router.visit(`/rentals/new?customer_id=${props.customer.id}`),
    primary: true,
  },
  {
    key: 'edit',
    label: t('common.edit'),
    icon: 'sign',
    onClick: () => router.visit(`/customers/${props.customer.id}/edit`),
  },
  {
    key: 'archive',
    label: t('customers.archive.confirm'),
    icon: 'x',
    variant: 'danger',
    onClick: () => { showArchiveConfirm.value = true },
  },
])

function confirmArchive() {
  showArchiveConfirm.value = false
  router.delete(`/customers/${props.customer.id}`)
}

const LOCALE_LABELS = { en: 'English', ru: 'Русский', th: 'ไทย', zh: '中文' }
function localeLabel(code) {
  if (!code) return t('customers.show.fields.preferredLocaleAuto')
  return LOCALE_LABELS[code] || code
}

function goBack() {
  if (typeof window !== "undefined" && window.history.length > 1) {
    window.history.back()
  } else {
    router.visit("/customers")
  }
}
</script>

<template>
  <PageShell :title="t('nav.customers')">
    <div class="flex items-center justify-between mb-4 gap-3">
      <div class="flex items-center gap-3 min-w-0">
        <button
          type="button"
          class="inline-flex items-center text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors"
          :aria-label="t('common.back')"
          @click="goBack"
        >
          <Icon
            name="chev-l"
            :size="18"
          />
        </button>
        <h2 class="text-xl font-semibold text-[color:var(--ink)] truncate">
          {{ customer.full_name }}
        </h2>
      </div>
      <ActionCluster :actions="headerActions" />
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Profile card -->
      <div
        class="lg:col-span-2 rounded-xl p-6"
        :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
      >
        <h2 class="text-lg font-semibold text-[color:var(--ink)] mb-4">
          {{ t('customers.show.contactDetails') }}
        </h2>
        <dl class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <dt class="text-[color:var(--muted)]">
              {{ t('customers.show.fields.email') }}
            </dt>
            <dd class="fw-value mt-0.5">
              <a
                v-if="customer.email"
                :href="`mailto:${customer.email}`"
                class="text-[color:var(--accent)] hover:underline"
              >{{ customer.email }}</a>
              <span
                v-else
                class="text-[color:var(--ink)]"
              >—</span>
            </dd>
          </div>
          <div>
            <dt class="text-[color:var(--muted)]">
              {{ t('customers.show.fields.phone') }}
            </dt>
            <dd class="fw-value mt-0.5">
              <a
                v-if="customer.phone"
                :href="`tel:${customer.phone}`"
                class="text-[color:var(--accent)] hover:underline"
              >{{ customer.phone }}</a>
              <span
                v-else
                class="text-[color:var(--ink)]"
              >—</span>
            </dd>
          </div>
          <div>
            <dt class="text-[color:var(--muted)]">
              {{ t('customers.show.fields.dateOfBirth') }}
            </dt>
            <dd class="fw-value text-[color:var(--ink)] mt-0.5">
              {{ customer.date_of_birth ? formatDate(customer.date_of_birth) : '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-[color:var(--muted)]">
              {{ t('customers.show.fields.nationality') }}
            </dt>
            <dd class="fw-value text-[color:var(--ink)] mt-0.5">
              {{ customer.nationality || '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-[color:var(--muted)]">
              {{ t('customers.show.fields.passportNo') }}
            </dt>
            <dd class="fw-value text-[color:var(--ink)] mt-0.5">
              {{ customer.passport_no || '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-[color:var(--muted)]">
              {{ t('customers.show.fields.preferredLocale') }}
            </dt>
            <dd class="fw-value text-[color:var(--ink)] mt-0.5">
              {{ localeLabel(customer.preferred_locale) }}
            </dd>
          </div>
          <div>
            <dt class="text-[color:var(--muted)]">
              {{ t('customers.show.fields.address') }}
            </dt>
            <dd class="font-medium text-[color:var(--ink)] mt-0.5 whitespace-pre-line">
              {{ customer.address || '—' }}
            </dd>
          </div>
        </dl>

        <h2 class="text-lg font-semibold text-[color:var(--ink)] mt-6 mb-4">
          {{ t('customers.show.licenceSection') }}
        </h2>
        <dl class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <dt class="text-[color:var(--muted)]">
              {{ t('customers.show.fields.licenceNumber') }}
            </dt>
            <dd class="fw-value text-[color:var(--ink)] mt-0.5">
              {{ customer.licence_number || '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-[color:var(--muted)]">
              {{ t('customers.show.fields.expiry') }}
            </dt>
            <dd class="mt-0.5 flex items-center gap-2">
              <span class="font-medium text-[color:var(--ink)]">
                {{ customer.licence_expiry ? formatDate(customer.licence_expiry) : '—' }}
              </span>
              <span
                v-if="customer.licence_status !== 'none'"
                class="px-2 py-0.5 rounded-full text-xs font-medium"
                :style="licenceBadgeStyle(customer.licence_status)"
              >
                {{ t(`customers.licence.${customer.licence_status}`) }}
              </span>
            </dd>
          </div>
          <div v-if="customer.licence_scan_url">
            <dt class="text-[color:var(--muted)]">
              {{ t('customers.show.fields.licenceScan') }}
            </dt>
            <dd class="mt-0.5">
              <a
                :href="customer.licence_scan_url"
                target="_blank"
                class="text-[color:var(--accent)] hover:underline text-sm"
              >{{ t('customers.show.viewDocument') }}</a>
            </dd>
          </div>
          <div v-if="customer.passport_photo_url">
            <dt class="text-[color:var(--muted)]">
              {{ t('customers.show.fields.passportPhoto') }}
            </dt>
            <dd class="mt-0.5">
              <a
                :href="customer.passport_photo_url"
                target="_blank"
                class="text-[color:var(--accent)] hover:underline text-sm"
              >{{ t('customers.show.viewDocument') }}</a>
            </dd>
          </div>
        </dl>
      </div>

      <!-- Sidebar column -->
      <div class="flex flex-col gap-6">
        <!-- Notes -->
        <div
          class="rounded-xl p-6"
          :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
        >
          <h2 class="text-lg font-semibold text-[color:var(--ink)] mb-3">
            {{ t('customers.show.notes') }}
          </h2>
          <p class="text-sm text-[color:var(--inkSoft)] whitespace-pre-line">
            {{ customer.notes || t('customers.show.noNotes') }}
          </p>
        </div>

        <!-- Lifetime value -->
        <div
          class="rounded-xl p-5"
          :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
        >
          <h2 class="text-sm font-semibold text-[color:var(--inkSoft)] mb-3">
            {{ t('customers.lifetime.title') }}
          </h2>
          <dl class="space-y-2">
            <div class="flex justify-between text-sm">
              <dt class="text-[color:var(--muted)]">
                {{ t('customers.lifetime.totalRentals') }}
              </dt>
              <dd class="fw-value tabular-nums text-[color:var(--ink)]">
                {{ lifetime_stats?.total_rentals ?? 0 }}
              </dd>
            </div>
            <div class="flex justify-between text-sm">
              <dt class="text-[color:var(--muted)]">
                {{ t('customers.lifetime.totalRevenue') }}
              </dt>
              <dd class="fw-value tabular-nums text-[color:var(--ink)]">
                {{ lifetime_stats?.total_revenue ?? '—' }}
              </dd>
            </div>
            <div class="flex justify-between text-sm">
              <dt class="text-[color:var(--muted)]">
                {{ t('customers.lifetime.avgDuration') }}
              </dt>
              <dd class="fw-value tabular-nums text-[color:var(--ink)]">
                {{ lifetime_stats?.avg_duration_days ?? 0 }} {{ t('common.days') }}
              </dd>
            </div>
          </dl>
        </div>

        <!-- Communication channels -->
        <div
          class="rounded-xl p-5"
          :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
        >
          <CustomerChannels
            :model-value="channels"
            :readonly="true"
          />
        </div>
      </div>
    </div>

    <!-- Rental history -->
    <div
      class="rounded-xl overflow-hidden mt-6"
      :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
    >
      <div
        class="px-6 py-4 flex items-center justify-between"
        :style="{ borderBottom: '1px solid var(--line)' }"
      >
        <h2 class="text-lg font-semibold text-[color:var(--ink)]">
          {{ t('customers.show.rentalHistory') }}
        </h2>
        <Link
          href="/rentals"
          class="text-sm text-[color:var(--accent)] hover:underline"
        >
          {{ t('common.viewAll') }}
        </Link>
      </div>
      <div
        v-if="!customer.rentals?.length"
        class="px-6 py-10 text-center text-sm text-[color:var(--muted)]"
      >
        {{ t('customers.show.noRentals') }}
      </div>
      <div v-else>
        <!-- Desktop: table -->
        <div class="hidden md:block overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr :style="{ background: 'var(--surfaceAlt)', borderBottom: '1px solid var(--line)' }">
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  #
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  {{ t('rentals.columns.vehicle') }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  {{ t('rental.show.fields.start') }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  {{ t('rental.show.fields.expectedReturn') }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  {{ t('rentals.columns.status') }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  {{ t('common.estimatedTotal') }}
                </th>
                <th class="relative px-4 py-3">
                  <span class="sr-only">{{ t('common.view') }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(r, i) in customer.rentals"
                :key="r.id"
                class="transition-colors hover:bg-[color:var(--surfaceAlt)]"
                :style="{ borderTop: i === 0 ? 'none' : '1px solid var(--line)' }"
              >
                <td class="px-4 py-3 whitespace-nowrap text-sm text-[color:var(--muted)] font-mono">
                  #{{ r.id }}
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm text-[color:var(--ink)]">
                  {{ r.vehicle_name }}
                  <span class="text-xs text-[color:var(--muted)] font-mono ml-1">{{ r.vehicle_reg }}</span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm text-[color:var(--inkSoft)]">
                  {{ formatDate(r.start_at) }}
                </td>
                <td
                  class="px-4 py-3 whitespace-nowrap text-sm"
                  :class="r.overdue ? 'text-[color:var(--danger)] font-medium' : 'text-[color:var(--inkSoft)]'"
                >
                  {{ formatDate(r.expected_end_at) }}
                  <span
                    v-if="r.overdue"
                    class="ml-1 text-xs px-1.5 py-0.5 rounded-full"
                    :style="{ background: 'var(--dangerSoft)', color: 'var(--danger)' }"
                  >
                    {{ t('rentals.status.overdue') }}
                  </span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap">
                  <StatusBadge
                    type="rental"
                    :status="r.status"
                    :overdue="r.overdue"
                  />
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm text-[color:var(--ink)]">
                  {{ r.estimated_total }}
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-right text-sm">
                  <Link
                    :href="`/rentals/${r.id}`"
                    class="text-[color:var(--accent)] hover:underline"
                  >
                    {{ t('common.view') }}
                  </Link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Mobile: cards -->
        <div class="md:hidden divide-y divide-[color:var(--line)]">
          <Link
            v-for="r in customer.rentals"
            :key="r.id"
            :href="`/rentals/${r.id}`"
            class="block px-4 py-3 hover:bg-[color:var(--surfaceAlt)] transition-colors"
          >
            <div class="flex items-start justify-between gap-2 mb-1.5">
              <div class="min-w-0 flex-1">
                <div class="text-sm fw-value text-[color:var(--ink)] truncate">
                  {{ r.vehicle_name }}
                  <span class="text-xs text-[color:var(--muted)] font-mono ml-1">{{ r.vehicle_reg }}</span>
                </div>
                <div class="text-xs text-[color:var(--muted)] font-mono mt-0.5">
                  #{{ r.id }}
                </div>
              </div>
              <StatusBadge
                type="rental"
                :status="r.status"
                :overdue="r.overdue"
              />
            </div>
            <div class="flex items-center justify-between text-xs gap-2">
              <span :class="r.overdue ? 'text-[color:var(--danger)] font-medium' : 'text-[color:var(--inkSoft)]'">
                {{ formatDate(r.start_at) }} → {{ formatDate(r.expected_end_at) }}
              </span>
              <span class="tabular-nums text-[color:var(--ink)] fw-value shrink-0">{{ r.estimated_total }}</span>
            </div>
          </Link>
        </div>
      </div>
    </div>

    <ConfirmDialog
      :open="showArchiveConfirm"
      :title="t('customers.archive.title')"
      :consequence="t('customers.archive.consequence')"
      :confirm-text="t('customers.archive.confirm')"
      @confirm="confirmArchive"
      @cancel="showArchiveConfirm = false"
    />
  </PageShell>
</template>
